import {
  CashEventDto,
  CashReconciliationDto,
  TillStateDto,
  ExpectedCashCalculationDto,
  CashZoneDto,
  CreateCashEventRequest,
  OpenTillRequest,
  CloseTillRequest,
  ApproveCashReconciliationRequest,
  CreateCashDropRequest,
  ReverseCashEventRequest,
  calculateVariance,
  calculateVariancePercentage,
  isVarianceOverThreshold,
} from './cashTransactionTypes';
import { ICashTransactionClient } from './ICashTransactionClient';

/**
 * Mocked implementation for UI development
 */
export class CashTransactionClientMocked implements ICashTransactionClient {
  private cashEvents: CashEventDto[] = [];
  private cashZones: CashZoneDto[] = [];
  private tillStates: TillStateDto[] = [];
  private reconciliations: CashReconciliationDto[] = [];
  private nextEventId = 1;
  private nextZoneId = 1;
  private nextReconciliationId = 1;

  constructor() {
    this.initializeMockData();
  }

  private initializeMockData() {
    // Mock cash zones
    this.cashZones = [
      {
        id: 1,
        storeNumber: '001',
        zoneName: 'Till 1',
        zoneType: 'Till',
        isActive: true,
        requiresTwoPersonApproval: false,
        varianceThresholdAmount: 10.00,
        rowNumber: 1,
      },
      {
        id: 2,
        storeNumber: '001',
        zoneName: 'Till 2',
        zoneType: 'Till',
        isActive: true,
        requiresTwoPersonApproval: false,
        varianceThresholdAmount: 10.00,
        rowNumber: 2,
      },
      {
        id: 3,
        storeNumber: '001',
        zoneName: 'Safe',
        zoneType: 'Safe',
        isActive: true,
        requiresTwoPersonApproval: true,
        varianceThresholdAmount: 50.00,
        rowNumber: 3,
      },
      {
        id: 4,
        storeNumber: '001',
        zoneName: 'Back Office',
        zoneType: 'BackOffice',
        isActive: true,
        requiresTwoPersonApproval: true,
        varianceThresholdAmount: 100.00,
        rowNumber: 4,
      },
      {
        id: 5,
        storeNumber: '001',
        zoneName: 'Till 3',
        zoneType: 'Till',
        isActive: true,
        requiresTwoPersonApproval: false,
        varianceThresholdAmount: 10.00,
        rowNumber: 5,
      },
      {
        id: 6,
        storeNumber: '001',
        zoneName: 'Banking Desk',
        zoneType: 'Banking',
        isActive: true,
        requiresTwoPersonApproval: true,
        varianceThresholdAmount: 200.00,
        rowNumber: 6,
      },
    ];

    this.nextZoneId = 7;

    // Mock till states
    this.tillStates = [
      {
        tillNumber: 'TILL-01',
        zoneId: 1,
        storeNumber: '001',
        status: 'Open',
        currentBalance: 250.00,
        assignedUserId: 'user1',
        assignedUsername: 'John Smith',
        isLocked: false,
      },
      {
        tillNumber: 'TILL-02',
        zoneId: 2,
        storeNumber: '001',
        status: 'Closed',
        currentBalance: 0,
        lastReconciliationTimestamp: new Date(Date.now() - 86400000).toISOString(),
        isLocked: false,
      },
      {
        tillNumber: 'TILL-03',
        zoneId: 3,
        storeNumber: '001',
        status: 'Closed',
        currentBalance: 0,
        lastReconciliationTimestamp: new Date(Date.now() - 172800000).toISOString(),
        isLocked: false,
      },
    ];

    // Mock events
    const now = new Date();
    this.cashEvents = [
      {
        id: 1,
        eventType: 'TillOpen',
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        amount: 150.00,
        currencyCode: 'GBP',
        userId: 'user1',
        username: 'John Smith',
        timestamp: new Date(now.getTime() - 28800000).toISOString(), // 8 hours ago
        shiftId: 'shift-morning-001',
        notes: 'Opening float for morning shift',
        isReversed: false,
        metadata: {},
        createdAtUtc: new Date(now.getTime() - 28800000).toISOString(),
      },
      {
        id: 2,
        eventType: 'Sale',
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        amount: 25.50,
        currencyCode: 'GBP',
        userId: 'user1',
        username: 'John Smith',
        timestamp: new Date(now.getTime() - 25200000).toISOString(),
        referenceId: 'POS-TXN-12345',
        isReversed: false,
        metadata: { posTransactionId: 'POS-TXN-12345' },
        createdAtUtc: new Date(now.getTime() - 25200000).toISOString(),
      },
      {
        id: 3,
        eventType: 'CashDrop',
        storeNumber: '001',
        tillNumber: 'Till 1',
        zoneId: 1,
        amount: 100.00,
        currencyCode: 'GBP',
        userId: 'user1',
        username: 'John Smith',
        timestamp: new Date(now.getTime() - 14400000).toISOString(),
        notes: 'Cash drop to safe',
        isReversed: false,
        metadata: { toZoneId: 3, toZoneName: 'Safe' },
        createdAtUtc: new Date(now.getTime() - 14400000).toISOString(),
      },
    ];

    this.nextEventId = 4;

    // Mock reconciliation
    this.reconciliations = [
      {
        id: 1,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: now.toISOString().split('T')[0],
        shiftId: 'shift-morning-001',
        status: 'Open',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 28800000).toISOString(),
        openedByUserId: 'user1',
        openedByUsername: 'John Smith',
        expectedCashSales: 245.50,
        expectedTotalSales: 545.50,
        expectedCardSales: 300.00,
        expectedRefunds: 15.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 20.00,
        expectedCashDrops: 100.00,
        expectedClosingBalance: 260.50,
        countMethod: 'QuickTotal',
        variance: 0,
        variancePercentage: 0,
        isOverThreshold: false,
        requiresApproval: false,
        createdAtUtc: new Date(now.getTime() - 28800000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 28800000).toISOString(),
      },
      {
        id: 2,
        storeNumber: '001',
        tillNumber: 'TILL-02',
        zoneId: 2,
        reconciliationDate: new Date(now.getTime() - 86400000).toISOString().split('T')[0],
        shiftId: 'shift-evening-005',
        status: 'Reconciled',
        openingBalance: 200.00,
        openingTimestamp: new Date(now.getTime() - 115200000).toISOString(),
        openedByUserId: 'user2',
        openedByUsername: 'Sarah Johnson',
        expectedCashSales: 485.75,
        expectedTotalSales: 1245.75,
        expectedCardSales: 760.00,
        expectedRefunds: 25.50,
        expectedPaidIns: 50.00,
        expectedPaidOuts: 35.00,
        expectedCashDrops: 300.00,
        expectedClosingBalance: 375.25,
        declaredClosingBalance: 378.00,
        declaredTimestamp: new Date(now.getTime() - 86400000).toISOString(),
        declaredByUserId: 'user2',
        declaredByUsername: 'Sarah Johnson',
        countMethod: 'DenominationBreakdown',
        variance: 2.75,
        variancePercentage: 0.73,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 82800000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 115200000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 82800000).toISOString(),
      },
      {
        id: 3,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: new Date(now.getTime() - 86400000).toISOString().split('T')[0],
        shiftId: 'shift-afternoon-012',
        status: 'VarianceUnderReview',
        openingBalance: 180.00,
        openingTimestamp: new Date(now.getTime() - 129600000).toISOString(),
        openedByUserId: 'user3',
        openedByUsername: 'David Wilson',
        expectedCashSales: 625.40,
        expectedTotalSales: 1125.40,
        expectedCardSales: 500.00,
        expectedRefunds: 18.75,
        expectedPaidIns: 0,
        expectedPaidOuts: 45.00,
        expectedCashDrops: 400.00,
        expectedClosingBalance: 341.65,
        declaredClosingBalance: 328.50,
        declaredTimestamp: new Date(now.getTime() - 93600000).toISOString(),
        declaredByUserId: 'user3',
        declaredByUsername: 'David Wilson',
        countMethod: 'DenominationBreakdown',
        variance: -13.15,
        variancePercentage: -3.85,
        isOverThreshold: true,
        requiresApproval: true,
        notes: 'Large variance requires approval - possible missing cash drop',
        createdAtUtc: new Date(now.getTime() - 129600000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 93600000).toISOString(),
      },
      {
        id: 4,
        storeNumber: '001',
        tillNumber: 'TILL-03',
        zoneId: 3,
        reconciliationDate: new Date(now.getTime() - 172800000).toISOString().split('T')[0],
        shiftId: 'shift-morning-032',
        status: 'Reconciled',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 201600000).toISOString(),
        openedByUserId: 'user4',
        openedByUsername: 'Emma Davis',
        expectedCashSales: 320.80,
        expectedTotalSales: 820.80,
        expectedCardSales: 500.00,
        expectedRefunds: 10.00,
        expectedPaidIns: 25.00,
        expectedPaidOuts: 15.00,
        expectedCashDrops: 200.00,
        expectedClosingBalance: 270.80,
        declaredClosingBalance: 270.80,
        declaredTimestamp: new Date(now.getTime() - 172800000).toISOString(),
        declaredByUserId: 'user4',
        declaredByUsername: 'Emma Davis',
        countMethod: 'DenominationBreakdown',
        variance: 0,
        variancePercentage: 0,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 169200000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 201600000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 169200000).toISOString(),
      },
      {
        id: 5,
        storeNumber: '001',
        tillNumber: 'TILL-02',
        zoneId: 2,
        reconciliationDate: new Date(now.getTime() - 259200000).toISOString().split('T')[0],
        shiftId: 'shift-evening-048',
        status: 'Closed',
        openingBalance: 200.00,
        openingTimestamp: new Date(now.getTime() - 288000000).toISOString(),
        openedByUserId: 'user5',
        openedByUsername: 'James Miller',
        expectedCashSales: 540.25,
        expectedTotalSales: 1340.25,
        expectedCardSales: 800.00,
        expectedRefunds: 30.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 50.00,
        expectedCashDrops: 350.00,
        expectedClosingBalance: 310.25,
        declaredClosingBalance: 285.00,
        declaredTimestamp: new Date(now.getTime() - 259200000).toISOString(),
        declaredByUserId: 'user5',
        declaredByUsername: 'James Miller',
        countMethod: 'QuickTotal',
        variance: -25.25,
        variancePercentage: -8.14,
        isOverThreshold: true,
        requiresApproval: true,
        notes: 'Rejected - significant variance, recount required',
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 255600000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 288000000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 255600000).toISOString(),
      },
      {
        id: 6,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: new Date(now.getTime() - 345600000).toISOString().split('T')[0],
        shiftId: 'shift-morning-064',
        status: 'Reconciled',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 374400000).toISOString(),
        openedByUserId: 'user1',
        openedByUsername: 'John Smith',
        expectedCashSales: 395.60,
        expectedTotalSales: 895.60,
        expectedCardSales: 500.00,
        expectedRefunds: 20.00,
        expectedPaidIns: 30.00,
        expectedPaidOuts: 25.00,
        expectedCashDrops: 250.00,
        expectedClosingBalance: 280.60,
        declaredClosingBalance: 281.50,
        declaredTimestamp: new Date(now.getTime() - 345600000).toISOString(),
        declaredByUserId: 'user1',
        declaredByUsername: 'John Smith',
        countMethod: 'DenominationBreakdown',
        variance: 0.90,
        variancePercentage: 0.32,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager2',
        approvedByUsername: 'Lisa Anderson',
        approvedTimestamp: new Date(now.getTime() - 342000000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 374400000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 342000000).toISOString(),
      },
      {
        id: 7,
        storeNumber: '001',
        tillNumber: 'TILL-03',
        zoneId: 3,
        reconciliationDate: new Date(now.getTime() - 432000000).toISOString().split('T')[0],
        shiftId: 'shift-evening-080',
        status: 'Reconciled',
        openingBalance: 180.00,
        openingTimestamp: new Date(now.getTime() - 460800000).toISOString(),
        openedByUserId: 'user6',
        openedByUsername: 'Olivia Martinez',
        expectedCashSales: 455.90,
        expectedTotalSales: 1055.90,
        expectedCardSales: 600.00,
        expectedRefunds: 12.50,
        expectedPaidIns: 40.00,
        expectedPaidOuts: 30.00,
        expectedCashDrops: 300.00,
        expectedClosingBalance: 333.40,
        declaredClosingBalance: 335.00,
        declaredTimestamp: new Date(now.getTime() - 432000000).toISOString(),
        declaredByUserId: 'user6',
        declaredByUsername: 'Olivia Martinez',
        countMethod: 'DenominationBreakdown',
        variance: 1.60,
        variancePercentage: 0.48,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager2',
        approvedByUsername: 'Lisa Anderson',
        approvedTimestamp: new Date(now.getTime() - 428400000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 460800000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 428400000).toISOString(),
      },
      {
        id: 8,
        storeNumber: '001',
        tillNumber: 'TILL-02',
        zoneId: 2,
        reconciliationDate: new Date(now.getTime() - 518400000).toISOString().split('T')[0],
        shiftId: 'shift-morning-096',
        status: 'Reconciled',
        openingBalance: 200.00,
        openingTimestamp: new Date(now.getTime() - 547200000).toISOString(),
        openedByUserId: 'user2',
        openedByUsername: 'Sarah Johnson',
        expectedCashSales: 365.20,
        expectedTotalSales: 965.20,
        expectedCardSales: 600.00,
        expectedRefunds: 22.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 28.00,
        expectedCashDrops: 250.00,
        expectedClosingBalance: 265.20,
        declaredClosingBalance: 265.20,
        declaredTimestamp: new Date(now.getTime() - 518400000).toISOString(),
        declaredByUserId: 'user2',
        declaredByUsername: 'Sarah Johnson',
        countMethod: 'DenominationBreakdown',
        variance: 0,
        variancePercentage: 0,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 514800000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 547200000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 514800000).toISOString(),
      },
      {
        id: 9,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: new Date(now.getTime() - 604800000).toISOString().split('T')[0],
        shiftId: 'shift-evening-112',
        status: 'Reconciled',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 633600000).toISOString(),
        openedByUserId: 'user1',
        openedByUsername: 'John Smith',
        expectedCashSales: 512.45,
        expectedTotalSales: 1212.45,
        expectedCardSales: 700.00,
        expectedRefunds: 15.25,
        expectedPaidIns: 50.00,
        expectedPaidOuts: 35.00,
        expectedCashDrops: 350.00,
        expectedClosingBalance: 312.20,
        declaredClosingBalance: 314.00,
        declaredTimestamp: new Date(now.getTime() - 604800000).toISOString(),
        declaredByUserId: 'user1',
        declaredByUsername: 'John Smith',
        countMethod: 'DenominationBreakdown',
        variance: 1.80,
        variancePercentage: 0.58,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager2',
        approvedByUsername: 'Lisa Anderson',
        approvedTimestamp: new Date(now.getTime() - 601200000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 633600000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 601200000).toISOString(),
      },
      {
        id: 10,
        storeNumber: '001',
        tillNumber: 'TILL-03',
        zoneId: 3,
        reconciliationDate: new Date(now.getTime() - 691200000).toISOString().split('T')[0],
        shiftId: 'shift-afternoon-128',
        status: 'Reconciled',
        openingBalance: 180.00,
        openingTimestamp: new Date(now.getTime() - 720000000).toISOString(),
        openedByUserId: 'user6',
        openedByUsername: 'Olivia Martinez',
        expectedCashSales: 428.90,
        expectedTotalSales: 1028.90,
        expectedCardSales: 600.00,
        expectedRefunds: 18.50,
        expectedPaidIns: 25.00,
        expectedPaidOuts: 20.00,
        expectedCashDrops: 300.00,
        expectedClosingBalance: 295.40,
        declaredClosingBalance: 296.00,
        declaredTimestamp: new Date(now.getTime() - 691200000).toISOString(),
        declaredByUserId: 'user6',
        declaredByUsername: 'Olivia Martinez',
        countMethod: 'DenominationBreakdown',
        variance: 0.60,
        variancePercentage: 0.20,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 687600000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 720000000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 687600000).toISOString(),
      },
      {
        id: 11,
        storeNumber: '001',
        tillNumber: 'TILL-02',
        zoneId: 2,
        reconciliationDate: new Date(now.getTime() - 777600000).toISOString().split('T')[0],
        shiftId: 'shift-morning-144',
        status: 'VarianceUnderReview',
        openingBalance: 200.00,
        openingTimestamp: new Date(now.getTime() - 806400000).toISOString(),
        openedByUserId: 'user2',
        openedByUsername: 'Sarah Johnson',
        expectedCashSales: 585.75,
        expectedTotalSales: 1285.75,
        expectedCardSales: 700.00,
        expectedRefunds: 28.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 42.00,
        expectedCashDrops: 380.00,
        expectedClosingBalance: 335.75,
        declaredClosingBalance: 320.00,
        declaredTimestamp: new Date(now.getTime() - 777600000).toISOString(),
        declaredByUserId: 'user2',
        declaredByUsername: 'Sarah Johnson',
        countMethod: 'DenominationBreakdown',
        variance: -15.75,
        variancePercentage: -4.69,
        isOverThreshold: true,
        requiresApproval: true,
        notes: 'Variance under review - possible unrecorded cash drop',
        createdAtUtc: new Date(now.getTime() - 806400000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 777600000).toISOString(),
      },
      {
        id: 12,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: new Date(now.getTime() - 864000000).toISOString().split('T')[0],
        shiftId: 'shift-evening-160',
        status: 'Reconciled',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 892800000).toISOString(),
        openedByUserId: 'user1',
        openedByUsername: 'John Smith',
        expectedCashSales: 475.30,
        expectedTotalSales: 1175.30,
        expectedCardSales: 700.00,
        expectedRefunds: 19.80,
        expectedPaidIns: 30.00,
        expectedPaidOuts: 25.00,
        expectedCashDrops: 320.00,
        expectedClosingBalance: 290.50,
        declaredClosingBalance: 290.50,
        declaredTimestamp: new Date(now.getTime() - 864000000).toISOString(),
        declaredByUserId: 'user1',
        declaredByUsername: 'John Smith',
        countMethod: 'QuickTotal',
        variance: 0,
        variancePercentage: 0,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager2',
        approvedByUsername: 'Lisa Anderson',
        approvedTimestamp: new Date(now.getTime() - 860400000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 892800000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 860400000).toISOString(),
      },
      {
        id: 13,
        storeNumber: '001',
        tillNumber: 'TILL-03',
        zoneId: 3,
        reconciliationDate: new Date(now.getTime() - 950400000).toISOString().split('T')[0],
        shiftId: 'shift-afternoon-176',
        status: 'Reconciled',
        openingBalance: 180.00,
        openingTimestamp: new Date(now.getTime() - 979200000).toISOString(),
        openedByUserId: 'user4',
        openedByUsername: 'Emma Davis',
        expectedCashSales: 392.65,
        expectedTotalSales: 992.65,
        expectedCardSales: 600.00,
        expectedRefunds: 14.50,
        expectedPaidIns: 20.00,
        expectedPaidOuts: 18.00,
        expectedCashDrops: 280.00,
        expectedClosingBalance: 280.15,
        declaredClosingBalance: 282.00,
        declaredTimestamp: new Date(now.getTime() - 950400000).toISOString(),
        declaredByUserId: 'user4',
        declaredByUsername: 'Emma Davis',
        countMethod: 'DenominationBreakdown',
        variance: 1.85,
        variancePercentage: 0.66,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 946800000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 979200000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 946800000).toISOString(),
      },
      {
        id: 14,
        storeNumber: '001',
        tillNumber: 'TILL-02',
        zoneId: 2,
        reconciliationDate: new Date(now.getTime() - 1036800000).toISOString().split('T')[0],
        shiftId: 'shift-morning-192',
        status: 'Reconciled',
        openingBalance: 200.00,
        openingTimestamp: new Date(now.getTime() - 1065600000).toISOString(),
        openedByUserId: 'user5',
        openedByUsername: 'James Miller',
        expectedCashSales: 548.20,
        expectedTotalSales: 1348.20,
        expectedCardSales: 800.00,
        expectedRefunds: 25.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 35.00,
        expectedCashDrops: 370.00,
        expectedClosingBalance: 318.20,
        declaredClosingBalance: 319.50,
        declaredTimestamp: new Date(now.getTime() - 1036800000).toISOString(),
        declaredByUserId: 'user5',
        declaredByUsername: 'James Miller',
        countMethod: 'DenominationBreakdown',
        variance: 1.30,
        variancePercentage: 0.41,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager2',
        approvedByUsername: 'Lisa Anderson',
        approvedTimestamp: new Date(now.getTime() - 1033200000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 1065600000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 1033200000).toISOString(),
      },
      {
        id: 15,
        storeNumber: '001',
        tillNumber: 'TILL-01',
        zoneId: 1,
        reconciliationDate: new Date(now.getTime() - 1123200000).toISOString().split('T')[0],
        shiftId: 'shift-evening-208',
        status: 'Reconciled',
        openingBalance: 150.00,
        openingTimestamp: new Date(now.getTime() - 1152000000).toISOString(),
        openedByUserId: 'user3',
        openedByUsername: 'David Wilson',
        expectedCashSales: 438.80,
        expectedTotalSales: 1138.80,
        expectedCardSales: 700.00,
        expectedRefunds: 16.75,
        expectedPaidIns: 40.00,
        expectedPaidOuts: 28.00,
        expectedCashDrops: 310.00,
        expectedClosingBalance: 274.05,
        declaredClosingBalance: 275.00,
        declaredTimestamp: new Date(now.getTime() - 1123200000).toISOString(),
        declaredByUserId: 'user3',
        declaredByUsername: 'David Wilson',
        countMethod: 'DenominationBreakdown',
        variance: 0.95,
        variancePercentage: 0.35,
        isOverThreshold: false,
        requiresApproval: false,
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 1119600000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 1152000000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 1119600000).toISOString(),
      },
      {
        id: 16,
        storeNumber: '001',
        tillNumber: 'TILL-03',
        zoneId: 3,
        reconciliationDate: new Date(now.getTime() - 1209600000).toISOString().split('T')[0],
        shiftId: 'shift-morning-224',
        status: 'Closed',
        openingBalance: 180.00,
        openingTimestamp: new Date(now.getTime() - 1238400000).toISOString(),
        openedByUserId: 'user6',
        openedByUsername: 'Olivia Martinez',
        expectedCashSales: 325.50,
        expectedTotalSales: 925.50,
        expectedCardSales: 600.00,
        expectedRefunds: 20.00,
        expectedPaidIns: 0,
        expectedPaidOuts: 22.00,
        expectedCashDrops: 240.00,
        expectedClosingBalance: 223.50,
        declaredClosingBalance: 202.00,
        declaredTimestamp: new Date(now.getTime() - 1209600000).toISOString(),
        declaredByUserId: 'user6',
        declaredByUsername: 'Olivia Martinez',
        countMethod: 'QuickTotal',
        variance: -21.50,
        variancePercentage: -9.62,
        isOverThreshold: true,
        requiresApproval: true,
        notes: 'Closed - significant shortage, requires investigation',
        approvedByUserId: 'manager1',
        approvedByUsername: 'Michael Brown',
        approvedTimestamp: new Date(now.getTime() - 1206000000).toISOString(),
        createdAtUtc: new Date(now.getTime() - 1238400000).toISOString(),
        updatedAtUtc: new Date(now.getTime() - 1206000000).toISOString(),
      },
    ];

    this.nextReconciliationId = 17;
  }

  async getCashEvents(
    storeNumber: string,
    startDate: string,
    endDate: string,
    tillNumber?: string,
  ): Promise<CashEventDto[]> {
    await this.simulateDelay();
    
    let filtered = this.cashEvents.filter(e => e.storeNumber === storeNumber);
    
    if (tillNumber) {
      filtered = filtered.filter(e => e.tillNumber === tillNumber);
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    filtered = filtered.filter(e => {
      const eventDate = new Date(e.timestamp);
      return eventDate >= start && eventDate <= end;
    });
    
    return filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }

  async getCashEventById(eventId: number): Promise<CashEventDto> {
    await this.simulateDelay();
    const event = this.cashEvents.find(e => e.id === eventId);
    if (!event) throw new Error(`Event ${eventId} not found`);
    return event;
  }

  async createCashEvent(request: CreateCashEventRequest): Promise<CashEventDto> {
    await this.simulateDelay();
    
    const event: CashEventDto = {
      id: this.nextEventId++,
      eventType: request.eventType,
      storeNumber: request.storeNumber,
      tillNumber: request.tillNumber,
      zoneId: request.zoneId,
      amount: request.amount,
      currencyCode: 'GBP',
      userId: 'current-user',
      username: 'Current User',
      timestamp: new Date().toISOString(),
      shiftId: request.shiftId,
      referenceId: request.referenceId,
      notes: request.notes,
      isReversed: false,
      metadata: request.metadata || {},
      createdAtUtc: new Date().toISOString(),
    };
    
    this.cashEvents.push(event);
    return event;
  }

  async reverseCashEvent(request: ReverseCashEventRequest): Promise<CashEventDto> {
    await this.simulateDelay();
    
    const originalEvent = this.cashEvents.find(e => e.id === request.eventIdToReverse);
    if (!originalEvent) throw new Error('Event not found');
    
    originalEvent.isReversed = true;
    
    const reversalEvent: CashEventDto = {
      ...originalEvent,
      id: this.nextEventId++,
      reversalOfEventId: request.eventIdToReverse,
      amount: -originalEvent.amount,
      timestamp: new Date().toISOString(),
      notes: `Reversal: ${request.reason}. ${request.notes || ''}`,
      isReversed: false,
      createdAtUtc: new Date().toISOString(),
    };
    
    this.cashEvents.push(reversalEvent);
    return reversalEvent;
  }

  async getCashZones(storeNumber: string): Promise<CashZoneDto[]> {
    await this.simulateDelay();
    // Return all zones for mocking purposes (store number doesn't matter in dev)
    return this.cashZones;
  }

  async getCashZoneById(zoneId: number): Promise<CashZoneDto> {
    await this.simulateDelay();
    const zone = this.cashZones.find(z => z.id === zoneId);
    if (!zone) throw new Error('Zone not found');
    return zone;
  }

  async createCashZone(zone: Omit<CashZoneDto, 'id'>): Promise<CashZoneDto> {
    await this.simulateDelay();
    const newZone: CashZoneDto = {
      ...zone,
      id: this.nextZoneId++,
    };
    this.cashZones.push(newZone);
    return newZone;
  }

  async updateCashZone(zoneId: number, updates: Partial<CashZoneDto>): Promise<CashZoneDto> {
    await this.simulateDelay();
    const index = this.cashZones.findIndex(z => z.id === zoneId);
    if (index === -1) throw new Error('Zone not found');
    
    this.cashZones[index] = { ...this.cashZones[index], ...updates };
    return this.cashZones[index];
  }

  async deleteCashZone(zoneId: number): Promise<void> {
    await this.simulateDelay();
    const index = this.cashZones.findIndex(z => z.id === zoneId);
    if (index === -1) throw new Error('Zone not found');
    this.cashZones.splice(index, 1);
  }

  async getTillState(storeNumber: string, tillNumber: string): Promise<TillStateDto> {
    await this.simulateDelay();
    const state = this.tillStates.find(t => t.storeNumber === storeNumber && t.tillNumber === tillNumber);
    if (!state) throw new Error('Till state not found');
    return state;
  }

  async getAllTillStates(storeNumber: string): Promise<TillStateDto[]> {
    await this.simulateDelay();
    // For mocking purposes, return all till states regardless of store
    return this.tillStates;
  }

  async openTill(request: OpenTillRequest): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    // Create till open event
    await this.createCashEvent({
      eventType: 'TillOpen',
      storeNumber: request.storeNumber,
      tillNumber: request.tillNumber,
      zoneId: request.zoneId,
      amount: request.openingBalance,
      shiftId: request.shiftId,
      notes: request.notes,
    });
    
    // Update till state
    const tillStateIndex = this.tillStates.findIndex(
      t => t.storeNumber === request.storeNumber && t.tillNumber === request.tillNumber,
    );
    
    if (tillStateIndex !== -1) {
      this.tillStates[tillStateIndex].status = 'Open';
      this.tillStates[tillStateIndex].currentBalance = request.openingBalance;
      this.tillStates[tillStateIndex].currentShiftId = request.shiftId;
      this.tillStates[tillStateIndex].isLocked = false;
    } else {
      this.tillStates.push({
        tillNumber: request.tillNumber,
        zoneId: request.zoneId,
        storeNumber: request.storeNumber,
        status: 'Open',
        currentBalance: request.openingBalance,
        currentShiftId: request.shiftId,
        assignedUserId: 'current-user',
        assignedUsername: 'Current User',
        isLocked: false,
      });
    }
    
    // Create reconciliation record
    const reconciliation: CashReconciliationDto = {
      id: this.nextReconciliationId++,
      storeNumber: request.storeNumber,
      tillNumber: request.tillNumber,
      zoneId: request.zoneId,
      reconciliationDate: new Date().toISOString().split('T')[0],
      shiftId: request.shiftId,
      status: 'Open',
      openingBalance: request.openingBalance,
      openingTimestamp: new Date().toISOString(),
      openedByUserId: 'current-user',
      openedByUsername: 'Current User',
      expectedCashSales: 0,
      expectedTotalSales: 0,
      expectedCardSales: 0,
      expectedRefunds: 0,
      expectedPaidIns: 0,
      expectedPaidOuts: 0,
      expectedCashDrops: 0,
      expectedClosingBalance: request.openingBalance,
      denominationBreakdown: request.denominationBreakdown,
      countMethod: 'QuickTotal',
      variance: 0,
      variancePercentage: 0,
      isOverThreshold: false,
      requiresApproval: false,
      createdAtUtc: new Date().toISOString(),
      updatedAtUtc: new Date().toISOString(),
    };
    
    this.reconciliations.push(reconciliation);
    return reconciliation;
  }

  async closeTill(request: CloseTillRequest): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    const reconciliation = this.reconciliations.find(r => r.id === request.reconciliationId);
    if (!reconciliation) throw new Error('Reconciliation not found');
    
    const variance = calculateVariance(reconciliation.expectedClosingBalance, request.declaredClosingBalance);
    const variancePercentage = calculateVariancePercentage(
      reconciliation.expectedClosingBalance,
      request.declaredClosingBalance,
    );
    
    const zone = this.cashZones.find(z => z.id === reconciliation.zoneId);
    const isOverThreshold = zone ? isVarianceOverThreshold(variance, zone.varianceThresholdAmount) : false;
    
    reconciliation.declaredClosingBalance = request.declaredClosingBalance;
    reconciliation.declaredTimestamp = new Date().toISOString();
    reconciliation.declaredByUserId = 'current-user';
    reconciliation.declaredByUsername = 'Current User';
    reconciliation.denominationBreakdown = request.denominationBreakdown;
    reconciliation.countMethod = request.countMethod;
    reconciliation.variance = variance;
    reconciliation.variancePercentage = variancePercentage;
    reconciliation.isOverThreshold = isOverThreshold;
    reconciliation.requiresApproval = isOverThreshold || (zone?.requiresTwoPersonApproval ?? false);
    reconciliation.status = isOverThreshold ? 'VarianceUnderReview' : 'Counting';
    reconciliation.notes = request.notes;
    reconciliation.updatedAtUtc = new Date().toISOString();
    
    return reconciliation;
  }

  async createCashDrop(request: CreateCashDropRequest): Promise<CashEventDto> {
    await this.simulateDelay();
    
    return this.createCashEvent({
      eventType: 'CashDrop',
      storeNumber: request.storeNumber,
      tillNumber: request.fromTillNumber,
      amount: request.amount,
      referenceId: request.referenceId,
      notes: request.notes,
      metadata: { toZoneId: request.toZoneId },
    });
  }

  async getCashReconciliations(
    storeNumber: string,
    startDate: string,
    endDate: string,
    status?: string,
  ): Promise<CashReconciliationDto[]> {
    await this.simulateDelay();
    
    // For mocking purposes, return all reconciliations regardless of store
    let filtered = this.reconciliations;
    
    if (status) {
      filtered = filtered.filter(r => r.status === status);
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    filtered = filtered.filter(r => {
      const reconDate = new Date(r.reconciliationDate);
      return reconDate >= start && reconDate <= end;
    });
    
    return filtered.sort((a, b) => new Date(b.reconciliationDate).getTime() - new Date(a.reconciliationDate).getTime());
  }

  async getCashReconciliationById(reconciliationId: number): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    const recon = this.reconciliations.find(r => r.id === reconciliationId);
    if (!recon) throw new Error('Reconciliation not found');
    return recon;
  }

  async updateCashReconciliation(
    reconciliationId: number,
    updates: Partial<CashReconciliationDto>,
  ): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    const index = this.reconciliations.findIndex(r => r.id === reconciliationId);
    if (index === -1) throw new Error('Reconciliation not found');
    
    this.reconciliations[index] = {
      ...this.reconciliations[index],
      ...updates,
      updatedAtUtc: new Date().toISOString(),
    };
    
    return this.reconciliations[index];
  }

  async submitCashReconciliation(reconciliationId: number): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    return this.updateCashReconciliation(reconciliationId, {
      status: 'Submitted',
    });
  }

  async approveCashReconciliation(request: ApproveCashReconciliationRequest): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    const reconciliation = await this.getCashReconciliationById(request.reconciliationId);
    
    return this.updateCashReconciliation(request.reconciliationId, {
      status: 'Reconciled',
      approvedByUserId: 'manager-user',
      approvedByUsername: 'Manager User',
      approvedTimestamp: new Date().toISOString(),
      notes: reconciliation.notes ? `${reconciliation.notes}\n\nApproval: ${request.approvalNotes || 'Approved'}` : request.approvalNotes,
    });
  }

  async getExpectedCash(
    storeNumber: string,
    tillNumber: string,
    startDate: string,
    endDate: string,
  ): Promise<ExpectedCashCalculationDto> {
    await this.simulateDelay();
    
    // Mock calculation - in real system, this comes from POS integration
    return {
      storeNumber,
      tillNumber,
      startTimestamp: startDate,
      endTimestamp: endDate,
      openingBalance: 150.00,
      cashSales: 523.45,
      cardSales: 876.32,
      refunds: 25.50,
      paidIns: 0,
      paidOuts: 40.00,
      cashDrops: 200.00,
      expectedClosing: 407.95,
      transactionCount: 47,
    };
  }

  async startVarianceInvestigation(reconciliationId: number, notes: string): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    return this.updateCashReconciliation(reconciliationId, {
      investigationStatus: 'InProgress',
      investigationNotes: notes,
    });
  }

  async resolveVarianceInvestigation(reconciliationId: number, resolution: string): Promise<CashReconciliationDto> {
    await this.simulateDelay();
    
    const reconciliation = await this.getCashReconciliationById(reconciliationId);
    
    return this.updateCashReconciliation(reconciliationId, {
      investigationStatus: 'Resolved',
      investigationNotes: reconciliation.investigationNotes 
        ? `${reconciliation.investigationNotes}\n\nResolution: ${resolution}` 
        : resolution,
    });
  }

  private simulateDelay(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 200));
  }
}
